import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { CardDataSource, ICardReaderStore } from "modules/cardReader/domain/interfaces/IUseCardReaderStore";
import { useCardReaderStore } from "modules/cardReader/infra/store/CardReaderStore";
import { useKiosk } from "../../../application/contexts/kiosk/KioskContext";
import { useUi } from "../../../application/contexts/ui/UIContext";
import {
  ICardCashless,
  ICardGroupDiscounts,
  IFullCardData,
} from "../../../domains/cashless/aggregates/ICardCashless";
import { IOrderPad } from "../../../domains/cashless/aggregates/IOrderPad";
import { SystemType } from "../../../domains/kiosk/aggregates/Kiosk";
import { CashlessRepository } from "../../../Infrastructure/repositories/api/CashlessRepository";
import { keyBlockedCards } from "../../../Infrastructure/repositories/api/KioskRepository";
import { isReactNative } from "utils/constants";
import { useHistory } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { IOfflineCardData } from "modules/cardReader/domain/models/IOfflineCardData";

interface ICardReaderContext {
  cardData: ICardCashless | null;
  orderPad: IOrderPad | null;
  tag: string | null;
  enableSensor: (onlyUid?: boolean, disableBeep?: boolean) => Promise<void>;
  disableSensor: () => void;
  clearCardData: () => void;
  offlineCardData: IFullCardData | null;
  readErrorMessage: string | null;
  outOfDateError: string | null;
  cardDetected: boolean;
  getNominalCardDataWithName: (name: string) => Promise<void>;
  getNominalCardData: (tag: string) => Promise<void>;
  isNominalData: React.MutableRefObject<boolean>;
  isOnlineRecharge: React.MutableRefObject<boolean>;
  isPaying: React.MutableRefObject<boolean>;
  groupDiscounts: ICardGroupDiscounts[] | null;
  readedCard: string | null;
  setReadedCard: (readedCard: string | null) => void;
  isClosed?: boolean;
  setIsClosed: (isClosed: boolean | undefined) => void;
  setOfflineCardData: (offlineCardData: IOfflineCardData) => void;
}
//container
const cashlessRepository = CashlessRepository();

export const CardReaderContext = createContext<ICardReaderContext>(
  {} as ICardReaderContext
);

export const CardReaderProvider: FC = ({ children }) => {
  const { kiosk } = useKiosk();
  const { showLoading, hideLoading, toast } = useUi();
  const { addLog } = useAppInsights();
  const [
    tag,
    readType,
    cardDetected,
    _enableSensor,
    disableSensor,
    readErrorMessage,
    offlineCardData,
    clearCardReader,
    readTag,
    isActiveSensor,
    isClosed,
    setIsClosed,
    cardData,
    groupDiscounts,
    orderPad,
    outOfDateError,
    readedCard,
    setReadedCard,
    setCardData,
    setGroupDiscounts,
    setOrderPad,
    setOutOfDateError,
    setCardDataSource,
    isOfflineRechargeRecordMode,
    setOfflineCardData,
  ] = useCardReaderStore(useShallow((state: ICardReaderStore) => [
    state.tag,
    state.readType,
    state.cardDetected,
    state.enableSensor,
    state.disableSensor,
    state.readError,
    state.offlineCardData,
    state.clearCardReader,
    state.readTag,
    state.isActiveSensor,
    state.isClosed,
    state.setIsClosed,
    state.cardData,
    state.groupDiscounts,
    state.orderPad,
    state.outOfDateError,
    state.readedCard,
    state.setReadedCard,
    state.setCardData,
    state.setGroupDiscounts,
    state.setOrderPad,
    state.setOutOfDateError,
    state.setCardDataSource,
    state.isOfflineRechargeRecordMode,
    state.setOfflineCardData,
  ]));

  //const [cardData, setCardData] = useState<ICardCashless | null>(null);
  //const [groupDiscounts, setGroupDiscounts] = useState<
  //  ICardGroupDiscounts[] | null
  //>(null);
  //const [orderPad, setOrderPad] = useState<IOrderPad | null>(null);
  //const [outOfDateError, setOutOfDateError] = useState<string | null>(null);
  //const [readedCard, setReadedCard] = useState<string | null>(null);

  const isNominalData = useRef<boolean>(false);
  const isOnlineRecharge = useRef<boolean>(false);
  const isPaying = useRef<boolean>(false);
  const firstReadTag = useRef(true);
  const history = useHistory();


  const enableSensor = useCallback(async (onlyUid?: boolean, disableBeep?: boolean) => {
    try {
      if (isReactNative) {
        console.log("readTag");
        await readTag("Cashless", onlyUid, disableBeep);
      } else {
        console.log("enableSensor");
        await _enableSensor("Cashless", kiosk?.cashlessCode);
      }
    } catch (error) {

      addLog("cardData", { message: "Falha na ativação cashless", error });
      setOutOfDateError(String(error));
      if ((error as any).message.includes("WAIT_TIMEOUT")) {
        //toast((error as any)?.message, "error");
        history.push("/");
      }
    }
  }, [readTag, _enableSensor, kiosk, addLog, setOutOfDateError, history]);

  const isSystemType = useCallback(
    (systemType: SystemType) => {
      return kiosk?.configSymstem?.find(
        (configSymstem) => configSymstem.systemType === systemType
      )
        ? true
        : false;
    },
    [kiosk]
  );

  const clearCardData = useCallback(() => {
    isNominalData.current = false;
    isOnlineRecharge.current = false;
    isPaying.current = false;
    setReadedCard(null);
    setCardData(null);
    clearCardReader();
    setOrderPad(null);
    setOutOfDateError(null);
    setIsClosed(undefined);
  }, [setReadedCard, setCardData, clearCardReader, setOrderPad, setOutOfDateError, setIsClosed]);

  const getCardData = useCallback(
    async (tag: string) => {
      if (kiosk) {
        showLoading();
        try {
          if (isSystemType(SystemType.pospayOrderPad)) {
            const orderpads = await cashlessRepository.getOrdPads(
              kiosk.localId
            );
            const orderPad = orderpads.find(
              (_orderPad) =>
                _orderPad.numero.toUpperCase() === tag.toUpperCase()
            );
            if (orderPad) {
              setOrderPad(orderPad);
              const responseCardCasheless =
                await cashlessRepository.getCardData(
                  orderPad.id,
                  orderPad.numero
                );
              if (responseCardCasheless) {
                if (responseCardCasheless.status === 1) {
                  addLog("cardData", {
                    orderPad,
                    cardData: responseCardCasheless,
                  });
                  setCardData(responseCardCasheless);
                  if (responseCardCasheless.group) {
                    try {
                      const result = await cashlessRepository.getGroupData(
                        kiosk.localId,
                        responseCardCasheless.associationId,
                        tag
                      );
                      setGroupDiscounts(result);
                    } catch (error) {
                      addLog("cardData", {
                        message: "Falha ao obter grupos de descontos",
                        error,
                      });
                      setGroupDiscounts(null);
                    }
                  } else {
                    setGroupDiscounts(null);
                  }
                } else {
                  addLog("cardData", {
                    message: "Cartão/Pulseira bloqueado",
                    tag: responseCardCasheless.tag,
                  });
                  toast("Cartão/Pulseira bloqueado", "error");
                  history.push("/");
                }
              } else {
                addLog("cardData", {
                  message: "Falha ao obter cardData",
                  orderPad,
                });
                history.push("/");
              }
            } else {
              setCardData(null);
              addLog("cardData", {
                message: "Cartão/Pulseira não encontrado",
                tag,
              });
              toast("Cartão/Pulseira não encontrado", "error");
              history.push("/");
            }
          } else {
            const responseCardCasheless = await cashlessRepository.getCardData(
              tag,
              tag
            );
            if (responseCardCasheless) {
              if (responseCardCasheless.status === 1) {
                if (responseCardCasheless.group) {
                  try {
                    const result = await cashlessRepository.getGroupData(
                      kiosk.localId,
                      responseCardCasheless.associationId,
                      tag
                    );
                    setGroupDiscounts(result);
                  } catch (error) {
                    setGroupDiscounts(null);
                    addLog("cardData", {
                      message: "Falha ao obter grupos de descontos",
                      error,
                    });
                  }
                } else {
                  setGroupDiscounts(null);
                }
                setCardData(responseCardCasheless);
              } else {
                toast("Cartão/Pulseira bloqueado", "error");
                history.push("/");
                addLog("cardData", {
                  message: "Cartão/Pulseira bloqueado",
                  responseCardCasheless,
                });
              }
            } else {
              addLog("cardData", {
                message: "Falha ao obter cardData",
                orderPad,
              });
            }
          }
        } catch (error) {
          toast(
            (error as any)?.response?.data?.message ??
            "Cartao/Pulseira não encontrado.",
            "error"
          );
          addLog("cardData", {
            message: "Cartão/Pulseira não encontrado",
            tag,
            error,
          });
          history.push("/");
        }
        hideLoading();
      }
    },
    [addLog, hideLoading, history, isSystemType, kiosk, orderPad, setCardData, setGroupDiscounts, setOrderPad, showLoading, toast]
  );

  const getNominalCardData = useCallback(
    async (tag: string) => {
      if (kiosk) {
        showLoading();
        try {
          const responseCardCasheless = await cashlessRepository.getCardData(
            tag,
            tag
          );
          if (responseCardCasheless) {
            if (responseCardCasheless.status === 1) {
              if (responseCardCasheless.group) {
                try {
                  const result = await cashlessRepository.getGroupData(
                    kiosk.localId,
                    responseCardCasheless.associationId,
                    tag
                  );
                  setGroupDiscounts(result);
                } catch (error) {
                  setGroupDiscounts(null);
                  addLog("cardData", {
                    message: "Falha ao obter grupos de descontos",
                    error,
                  });
                } finally {
                  setCardData(responseCardCasheless);
                }
              } else {
                setGroupDiscounts(null);
                setCardData(responseCardCasheless);
              }
            } else {
              addLog("cardData", {
                message: "Cartão/Pulseira inativo",
                responseCardCasheless,
              });
              toast("Cartão/Pulseira inativo", "error");  
              history.push("/");
            }
          }
        } catch (error) {
          addLog("cardData", {
            message: "Cartão/Pulseira não encontrado",
            tag,
            error,
          });
          toast(
            ((error as any)?.response?.data?.message) + "." || "Cartao não encontrado.",
            "error"
          );
          //go to home
          history.push("/");
        }
        hideLoading();
      }
    },
    [addLog, hideLoading, history, kiosk, setCardData, setGroupDiscounts, showLoading, toast]
  );

  const getNominalCardDataWithName = useCallback(
    async (name: string) => {
      if (kiosk) {
        showLoading();
        disableSensor(); //desativa o sensor para evitar timeout
        try {
          const responseCardCasheless =
            await cashlessRepository.getNominalExtractByNickname(name);
          if (responseCardCasheless) {
            if (
              responseCardCasheless.status === 1
              //|| responseCardCasheless.status === 3
            ) {
              if (responseCardCasheless.group) {
                try {
                  const result = await cashlessRepository.getGroupData(
                    kiosk.localId,
                    responseCardCasheless.associationId,
                    responseCardCasheless.tag
                  );
                  setGroupDiscounts(result);
                } catch (error) {
                  setGroupDiscounts(null);
                  addLog("cardData", {
                    message: "Falha ao obter grupos de descontos",
                    apelido: name,
                    error,
                  });
                } finally {
                  setCardData(responseCardCasheless);
                }
              } else {
                setGroupDiscounts(null);
                setCardData(responseCardCasheless);
              }
            } else {
              if (responseCardCasheless.status === 3) {
                toast("Solicitação de segunda via pendente", "error"); //TODO: Verificar se é isso mesmo
              } else {
                toast("Cartão inativo", "error");
              }
              addLog("cardData", {
                message: "Cartão/Pulseira inativo",
                responseCardCasheless,
                apelido: name,
              });
            }
          }
        } catch (error) {
          addLog("cardData", {
            message: "Cartão/Pulseira não encontrado",
            apelido: name,
            error,
          });
          toast(
            (error as any)?.response?.data?.message ??
            "Cartao/Pulseira não encontrado.",
            "error"
          );
        }
        hideLoading();
      }
    },
    [addLog, disableSensor, hideLoading, kiosk, setCardData, setGroupDiscounts, showLoading, toast]
  );

  useEffect(() => {
    if (!tag) {
      firstReadTag.current = true;
    }
  }, [tag]);

  useEffect(() => {
    const onReadTag = (tag: string) => {
      firstReadTag.current = false;

      const blockedRaw = localStorage.getItem(keyBlockedCards);
      const blockedCards: string[] = blockedRaw ? JSON.parse(blockedRaw) : [];
      if (blockedCards.some((blocked) => blocked.toLowerCase() === tag.toLowerCase())) {
        toast("Cartão/Pulseira bloqueado", "error");
        addLog("cardData", { message: "Tag bloqueada localmente", tag });
        return;
      }

      console.log("onReadTag",
        "cardData", cardData,
        "isOnlineRecharge", isOnlineRecharge.current,
        "kiosk?.doubleValidationOnlineRecharge", kiosk?.doubleValidationOnlineRecharge,
        "isPaying", isPaying.current,
        "tag", tag,
        "firstReadTag", firstReadTag.current,
        "readType", readType,
        "offlineCardData", offlineCardData,
        "kiosk.paymentNominalCashless", kiosk?.paymentNominalCashless,
        "onLineCashlessControl", kiosk?.onlineCashlessControl,
        "systemType", isSystemType(SystemType.pospayOrderPad)
      );

      addLog("cardData", { message: "onReadTag", tag });

      if (
        tag &&
        !isOfflineRechargeRecordMode &&
        (kiosk?.onlineCashlessControl ||
          isSystemType(
            SystemType.pospayOrderPad || kiosk?.paymentNominalCashless
          ))
      ) {
        //if (tag) {
        disableSensor();
        if (kiosk?.paymentNominalCashless) {
          setCardDataSource(CardDataSource.CardReader);
          getNominalCardData(tag);
        } else {
          if (!isPaying.current && !cardData) {
            console.log("getCardData", tag);
            getCardData(tag);
          }
        }
      }
    };
    if (tag && firstReadTag.current && readType === "Cashless") {
      console.log("onReadTag");
      onReadTag(tag);
    }
  }, [addLog, cardData, getCardData, getNominalCardData, isSystemType, kiosk, tag, readType, offlineCardData, enableSensor, disableSensor, setCardDataSource, isOfflineRechargeRecordMode]);

  return (
    <CardReaderContext.Provider
      value={{
        tag,
        isNominalData,
        isOnlineRecharge,
        isPaying,
        cardData,
        orderPad,
        isClosed,
        enableSensor,
        disableSensor,
        clearCardData,
        readErrorMessage,
        offlineCardData,
        outOfDateError,
        cardDetected,
        getNominalCardDataWithName,
        getNominalCardData,
        groupDiscounts,
        readedCard,
        setReadedCard,
        setIsClosed,
        setOfflineCardData
      }}
    >
      {children}
    </CardReaderContext.Provider>
  );
};

export const useCardReader = () => {
  const context = useContext(CardReaderContext);
  return context;
};